import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { handoverService } from "../../services/api";
import moment from 'moment';
import { 
  FaTimes, FaCar, FaUser, FaCalendarAlt, FaGasPump, FaTachometerAlt, 
  FaExclamationTriangle, FaClipboardCheck, FaCamera, FaVideo, 
  FaMicrophone, FaTrashAlt, FaExclamationCircle, FaCheck, FaEnvelope, FaPhone,
  FaArrowLeft, FaArrowRight, FaExpand, FaFileImage, FaSignature, FaEdit, FaSave, FaPlus, FaUpload
} from 'react-icons/fa';

const HandoverDetailsModal = ({ handoverId, onClose, onDelete = () => {} }) => {
  const [handover, setHandover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  
  const [expandedSections, setExpandedSections] = useState({
    condition: true,
    damages: true,
    documentation: true
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Add edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Add file upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('photo');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // State for photo carousel
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // State for signature preview modal
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [currentSignatureUrl, setCurrentSignatureUrl] = useState('');

  // Helper function to format media URLs properly
  const getMediaUrl = (url) => {
    if (!url) return '';
    
    // Log original URL for debugging
    console.log('Processing URL:', url);
    
    // If URL is already absolute with http/https, keep it as is
    if (url.startsWith('http')) {
      return url;
    }
    
    // If URL starts with /uploads, append the base URL without additional slash
    if (url.startsWith('/uploads')) {
      return `${'https://rent-server.create-site.gr'}${url}`;
    }
    
    // If URL starts with a slash but not /uploads
    if (url.startsWith('/')) {
      // Check if it's a handover path that should have /uploads prepended
      if (url.includes('/handovers/')) {
        return `${'https://rent-server.create-site.gr'}/uploads${url}`;
      }
      return `${'https://rent-server.create-site.gr'}${url}`;
    }
    
    // Otherwise, assume it's a relative path and add both the base URL and a leading slash
    if (url.startsWith('uploads/')) {
      return `${'https://rent-server.create-site.gr'}/${url}`;
    }
    
    // For all other cases, add /uploads/ as well
    return `${'https://rent-server.create-site.gr'}/uploads/${url}`;
  };
      useEffect(() => {
    const fetchHandoverData = async () => {
      try {
        setLoading(true);        const response = await handoverService.getHandover(handoverId);
        console.log('Handover data received:', response.data);
        
        // Log and check media file references
        console.log('Media files found:');
        if (response.data.photos) {
          console.log(`Photos (${response.data.photos.length}):`, response.data.photos);
        }
        if (response.data.videos) {
          console.log(`Videos (${response.data.videos.length}):`, response.data.videos);
        }
        if (response.data.voiceNotes) {
          console.log(`Voice notes (${response.data.voiceNotes.length}):`, response.data.voiceNotes);
        }
        
        setHandover(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching handover details:', err);
        setError('Failed to load handover details');
        setLoading(false);
      }
    };

    if (handoverId) {
      fetchHandoverData();
    }
  }, [handoverId, refreshKey]);
  
  // Function to manually refresh the handover data
  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };
    const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await handoverService.deleteHandover(handoverId);
      setDeleteLoading(false);
      
      // Check if we have details about deleted media files
      const deletedMediaCount = response.data?.deleted?.mediaCount || 0;
      const failedMediaCount = response.data?.deleted?.failedMediaCount || 0;
      
      // Log the deletion statistics
      console.log('Handover deletion complete:', {
        handoverId,
        deletedMediaCount,
        failedMediaCount,
        responseData: response.data
      });
      
      onDelete(handoverId, { 
        deletedMediaCount, 
        failedMediaCount 
      });
      onClose();
    } catch (err) {
      console.error('Error deleting handover:', err);
      setError('Failed to delete handover');
      setDeleteLoading(false);
    }
  };

  // Initialize edit data when handover loads
  useEffect(() => {
    if (handover) {
      setEditData({
        mileage: handover.mileage,
        fuelLevel: handover.fuelLevel,
        condition: {
          exterior: handover.condition?.exterior || 'good',
          interior: handover.condition?.interior || 'good',
          notes: handover.condition?.notes || ''
        },
        notes: handover.notes || '',
        damages: handover.damages || [],
        status: handover.status
      });
    }
  }, [handover]);
  // Handle edit save
  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const response = await handoverService.updateHandoverDetails(handoverId, editData);
      setHandover(response.data);
      setEditMode(false);
      setSaving(false);
    } catch (err) {
      console.error('Error updating handover:', err);
      setError('Failed to update handover');
      setSaving(false);
    }
  };

  // Handle money status toggle
  const handleMoneyStatusToggle = async () => {
    try {
      const newStatus = !handover.moneyHandedOver;
      
      // Update the backend
      await handoverService.updateMoneyStatus(handoverId, newStatus);
      
      // Update local state
      setHandover(prev => ({
        ...prev,
        moneyHandedOver: newStatus
      }));
      
      console.log(`Money status updated for handover ${handoverId}: ${newStatus}`);
    } catch (error) {
      console.error('Error updating money status:', error);
      setError('Failed to update money status');
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', uploadType);
      formData.append('caption', uploadCaption);

      const response = await handoverService.addDocumentation(handoverId, formData);
      setHandover(response.data);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadCaption('');
      setUploading(false);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file');
      setUploading(false);
    }
  };

  // Handle documentation removal
  const handleRemoveDocumentation = async (type, index) => {
    try {
      await handoverService.removeDocumentation(handoverId, type, index);
      handleRefresh(); // Refresh the data
    } catch (err) {
      console.error('Error removing documentation:', err);
      setError('Failed to remove documentation');
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };
  
  const modalVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring", 
        damping: 25, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      opacity: 0, 
      y: 50, 
      scale: 0.95,
      transition: { duration: 0.3 }
    }
  };
  
  const sectionVariants = {
    collapsed: { height: 0, opacity: 0 },
    expanded: { 
      height: "auto", 
      opacity: 1,
      transition: { 
        height: { duration: 0.3 },
        opacity: { duration: 0.3, delay: 0.1 }
      }
    }
  };

  // If loading, show loader
  if (loading) {
    return (
      <motion.div 
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div 
          className="bg-white rounded-2xl p-6 max-w-3xl w-11/12 shadow-2xl transform overflow-hidden"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center items-center h-64">
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-t-4 border-blue-600 border-r-4 border-transparent rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <motion.div 
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div 
          className="bg-white rounded-2xl p-6 max-w-3xl w-11/12 shadow-2xl transform overflow-hidden"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center mb-6">
            <div className="mr-4 text-red-500">
              <FaExclamationCircle className="w-10 h-10" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-red-600">Error</h3>
              <p className="text-gray-700">{error}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div className="text-center">
            <button
              onClick={onClose}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // If no handover data found
  if (!handover) {
    return (
      <motion.div 
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div 
          className="bg-white rounded-2xl p-6 max-w-3xl w-11/12 shadow-2xl transform overflow-hidden"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Handover Not Found
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-600">The requested handover details could not be found.</p>
          </div>
        </motion.div>
      </motion.div>
    );
  }
  
  // Determine the header gradient based on handover type
  const headerGradient = handover.handoverType === 'pickup' 
    ? 'from-green-500 to-blue-600' 
    : 'from-orange-500 to-pink-600';

  // Determine status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'disputed': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const openSignaturePreview = (url) => {
    setCurrentSignatureUrl(url);
    setShowSignaturePreview(true);
  };

  const closeSignaturePreview = () => {
    setShowSignaturePreview(false);
    setCurrentSignatureUrl('');
  };

  return (
    <motion.div 
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
      style={{ backdropFilter: 'blur(5px)' }}
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div 
        className="bg-white rounded-2xl shadow-2xl w-11/12 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${headerGradient} p-6 text-white relative overflow-hidden`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mt-20 -mr-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-10 rounded-full -mb-10 -ml-10"></div>
          
          <div className="flex justify-between items-start relative z-10 mb-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {handover.handoverType === 'pickup' ? 
                  'Vehicle Pickup Details' : 
                  'Vehicle Return Details'}
              </h2>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(handover.status)}`}>
                  {handover.status}
                </span>
                <span className="text-white opacity-90">
                  {moment(handover.handoverDate).format('DD/MM/YYYY HH:mm')}
                </span>
              </div>
            </div>
            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setEditMode(!editMode)}
                className={`p-2 rounded-full transition-colors flex items-center justify-center ${
                  editMode ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'
                }`}
                aria-label={editMode ? "Cancel edit" : "Edit handover"}
                title={editMode ? "Cancel edit" : "Edit handover"}
              >
                {editMode ? <FaTimes className="w-5 h-5" /> : <FaEdit className="w-5 h-5" />}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowUploadModal(true)}
                className="p-2 bg-green-500 hover:bg-green-600 rounded-full transition-colors flex items-center justify-center"
                aria-label="Add documentation"
                title="Add documentation"
              >
                <FaPlus className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                className="p-2 bg-blue-500 hover:bg-blue-600 rounded-full transition-colors flex items-center justify-center"
                aria-label="Refresh handover data"
                title="Refresh handover data"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="feather feather-refresh-cw"
                >
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 bg-red-500 hover:bg-red-600 rounded-full transition-colors flex items-center justify-center"
                aria-label="Delete handover"
              >
                <FaTrashAlt className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors flex items-center justify-center"
                aria-label="Close"
              >
                <FaTimes className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto flex-grow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vehicle and Basic Info - with edit functionality */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                  >
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200 flex justify-between items-center">
                    <h3 className="font-semibold text-blue-800 text-lg flex items-center">
                      <FaCar className="mr-2" />
                      Vehicle Information
                    </h3>
                    {editMode && (
                      <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center"
                      >
                      {saving ? (
                        <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-t-2 border-white border-r-2 border-transparent rounded-full mr-2"
                        />
                      ) : (
                        <FaSave className="mr-1" />
                      )}
                      Save
                      </button>
                    )}
                    </div>
                    <div className="p-6 space-y-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                      <FaCar className="text-blue-600" />
                      </div>
                      <div>
                      <p className="text-sm text-gray-500">Vehicle</p>
                      <p className="font-medium text-gray-800">
                        {handover.vehicle?.make} {handover.vehicle?.model}
                      </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                      <FaCalendarAlt className="text-blue-600" />
                      </div>
                      <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium text-gray-800">
                        {moment(handover.handoverDate).format('DD/MM/YYYY HH:mm')}
                      </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                      <FaTachometerAlt className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                      <p className="text-sm text-gray-500">Mileage</p>
                      {editMode ? (
                        <input
                        type="number"
                        value={editData.mileage}
                        onChange={(e) => setEditData(prev => ({ ...prev, mileage: parseInt(e.target.value) }))}
                        className="font-medium text-gray-800 border border-gray-300 rounded px-2 py-1 w-24"
                        />
                      ) : (
                        <p className="font-medium text-gray-800">{handover.mileage} km</p>
                      )}
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                      <FaGasPump className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                      <p className="text-sm text-gray-500">Fuel Level</p>
                      {editMode ? (
                        <select
                        value={editData.fuelLevel}
                        onChange={(e) => setEditData(prev => ({ ...prev, fuelLevel: e.target.value }))}
                        className="font-medium text-gray-800 border border-gray-300 rounded px-2 py-1"
                        >
                        <option value="">Select fuel level</option>
                        <option value="empty">Empty</option>
                        <option value="1/8">1/8</option>
                        <option value="2/8">2/8</option>
                        <option value="3/8">3/8</option>
                        <option value="4/8">4/8</option>
                        <option value="5/8">5/8</option>
                        <option value="6/8">6/8</option>
                        <option value="7/8">7/8</option>
                        <option value="full">Full</option>
                        </select>
                      ) : (
                        <div className='flex flex-row items-center'>
                        <div className="mt-1 h-6 bg-gray-200 rounded-full overflow-hidden w-full max-w-[150px]">
                          <motion.div 
                          initial={{ width: 0 }}
                          animate={{ 
                            width: (() => {
                                switch(handover.fuelLevel) {
                                  case 'empty': return '0%';
                                  case '1/8': return '12.5%';
                                  case '2/8': return '25%';
                                  case '3/8': return '37.5%';
                                  case '4/8': return '50%';
                                  case '5/8': return '62.5%';
                                  case '6/8': return '75%';
                                  case '7/8': return '87.5%';
                                  case 'full': return '100%';
                                  default: return '0%';
                                }
                              })()
                            }}
                            transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
                            className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                          ></motion.div>
                        </div>
                                                <p className="text-md font-medium text-gray-800 ml-4 mt-1 uppercase text-center">
                          {handover.fuelLevel}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Customer and Employee */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                  >
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200">
                    <h3 className="font-semibold text-purple-800 text-lg flex items-center">
                      <FaUser className="mr-2" />
                      Customer and Employee
                    </h3>
                    </div>
                    <div className="p-6 space-y-4">
                    <div>
                      <p className="text-sm text-purple-700 font-medium mb-2">Customer</p>
                      <div className="space-y-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <FaUser className="text-purple-600 text-sm" />
                        </div>
                        <p className="text-gray-800">{handover.customer.name}</p>
                      </div>
                      {handover.customer.email && (
                        <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                          <FaEnvelope className="text-purple-600 text-sm" />
                        </div>
                        <p className="text-gray-800">{handover.customer.email}</p>
                        </div>
                      )}
                      {handover.customer.phoneNumber && (
                        <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                          <FaPhone className="text-purple-600 text-sm" />
                        </div>
                        <p className="text-gray-800">{handover.customer.phoneNumber}</p>
                        </div>
                      )}
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-sm text-purple-700 font-medium mb-2">Employee</p>
                      <div className="space-y-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <FaUser className="text-purple-600 text-sm" />
                        </div>
                        <p className="text-gray-800">
                        {handover.employee?.firstName} {handover.employee?.lastName}
                        </p>
                      </div>
                      </div>
                    </div>
                    
                    {handover.signatures && (                <div className="border-t border-gray-100 pt-4">
                      <p className="text-sm text-purple-700 font-medium mb-2">Signatures</p>
                      <div className="flex space-x-6">
                        <div className="text-center">
                        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center 
                          ${handover.signatures.employee ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                        >
                          {handover.signatures.employee ? <FaCheck /> : <FaTimes />}
                        </div>
                        <p className="text-sm mt-1">Employee Signature</p>
                        {handover.signatures.employee && (
                          <button 
                          className="text-blue-600 text-xs mt-1 hover:underline"
                          onClick={() => openSignaturePreview(handover.signatures.employee)}
                          >
                          View Signature
                          </button>
                        )}
                        </div>
                        <div className="text-center">
                        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center 
                          ${handover.signatures.customer ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                        >
                          {handover.signatures.customer ? <FaCheck /> : <FaTimes />}
                        </div>
                        <p className="text-sm mt-1">Customer Signature</p>
                        {handover.signatures.customer && (
                          <button 
                          className="text-blue-600 text-xs mt-1 hover:underline"
                          onClick={() => openSignaturePreview(handover.signatures.customer)}
                          >
                          View Signature
                          </button>
                        )}
                        </div>
                      </div>
                      </div>                    )}
                    </div>
                  </motion.div>

                  {/* Money Status Section */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                  >
                    <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
                      <h3 className="font-semibold text-green-800 text-lg flex items-center">
                        <svg className="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                        </svg>
                        Money Status
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Money Handover Status</p>
                            <p className={`font-medium ${handover.moneyHandedOver ? 'text-green-600' : 'text-gray-500'}`}>
                              {handover.moneyHandedOver ? 'Money Handed Over' : 'Money Pending'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button
                            onClick={handleMoneyStatusToggle}
                            className={`relative inline-flex items-center h-8 rounded-full w-14 transition-colors duration-200 ease-in-out ${
                              handover.moneyHandedOver ? 'bg-green-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block w-6 h-6 transform bg-white rounded-full transition-transform duration-200 ease-in-out ${
                                handover.moneyHandedOver ? 'translate-x-7' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  </div>
                  
                  {/* Vehicle Condition Section - with edit functionality */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
          >            <div
              onClick={() => setExpandedSections(prev => ({ ...prev, condition: !prev.condition }))}
              className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200 cursor-pointer flex justify-between items-center"
            >
              <h3 className="font-semibold text-amber-800 text-lg flex items-center">
                <FaClipboardCheck className="mr-2" />
                Condition
              </h3>
              <motion.div
                animate={{ rotate: expandedSections.condition ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FaCheck className="text-amber-800" />
              </motion.div>
            </div>
            
            <AnimatePresence>
              {expandedSections.condition && (
                <motion.div 
                  variants={sectionVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  className="p-6 space-y-4 overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-amber-700 font-medium mb-2">Exterior Condition</p>
                      {editMode ? (
                        <select
                          value={editData.condition?.exterior}
                          onChange={(e) => setEditData(prev => ({ 
                            ...prev, 
                            condition: { ...prev.condition, exterior: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                          <option value="excellent">Excellent</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                          <option value="poor">Poor</option>
                        </select>
                      ) : (
                        <div className="p-4 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100">
                          <div className="flex items-center">
                            <motion.div 
                              className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 
                                ${handover.condition.exterior === 'excellent' ? 'bg-green-100 text-green-600' : 
                                  handover.condition.exterior === 'good' ? 'bg-blue-100 text-blue-600' :
                                  handover.condition.exterior === 'fair' ? 'bg-yellow-100 text-yellow-600' :
                                  'bg-red-100 text-red-600'}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.4, type: "spring" }}
                          >
                            <span className="text-2xl font-bold">
                              {handover.condition.exterior === 'excellent' ? 'A+' : 
                               handover.condition.exterior === 'good' ? 'B' :
                               handover.condition.exterior === 'fair' ? 'C' : 'D'}
                            </span>
                          </motion.div>
                          <div>
                            <p className="font-bold text-gray-800 capitalize">
                              {handover.condition.exterior}
                            </p>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-sm text-amber-700 font-medium mb-2">Interior Condition</p>
                      {editMode ? (
                        <select
                          value={editData.condition?.interior}
                          onChange={(e) => setEditData(prev => ({ 
                            ...prev, 
                            condition: { ...prev.condition, interior: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                          <option value="excellent">Excellent</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                          <option value="poor">Poor</option>
                        </select>
                      ) : (
                        <div className="p-4 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100">
                          <div className="flex items-center">
                            <motion.div 
                              className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 
                                ${handover.condition.interior === 'excellent' ? 'bg-green-100 text-green-600' : 
                                  handover.condition.interior === 'good' ? 'bg-blue-100 text-blue-600' :
                                  handover.condition.interior === 'fair' ? 'bg-yellow-100 text-yellow-600' :
                                  'bg-red-100 text-red-600'}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.5, type: "spring" }}
                          >
                            <span className="text-2xl font-bold">
                              {handover.condition.interior === 'excellent' ? 'A+' : 
                               handover.condition.interior === 'good' ? 'B' :
                               handover.condition.interior === 'fair' ? 'C' : 'D'}
                            </span>
                          </motion.div>
                          <div>
                            <p className="font-bold text-gray-800 capitalize">
                              {handover.condition.interior}
                            </p>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 mt-4">
                    <p className="text-sm text-amber-700 font-medium mb-1">Condition Notes</p>
                    {editMode ? (
                      <textarea
                        value={editData.condition?.notes}
                        onChange={(e) => setEditData(prev => ({ 
                          ...prev, 
                          condition: { ...prev.condition, notes: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 h-20"
                        placeholder="Add condition notes..."
                      />
                    ) : (
                      <p className="text-gray-800">{handover.condition?.notes || 'No notes'}</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          
          {/* Damages Section */}
          {handover.damages && handover.damages.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
            >              <div
                onClick={() => setExpandedSections(prev => ({ ...prev, damages: !prev.damages }))}
                className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 border-b border-red-200 cursor-pointer flex justify-between items-center"
              >
                <h3 className="font-semibold text-red-800 text-lg flex items-center">
                  <FaExclamationTriangle className="mr-2" />
                  Damages ({handover.damages.length})
                </h3>
                <motion.div
                  animate={{ rotate: expandedSections.damages ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaCheck className="text-red-800" />
                </motion.div>
              </div>
                <AnimatePresence>
                {expandedSections.damages && (
                  <motion.div 
                    variants={sectionVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    className="p-6 overflow-hidden"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {handover.damages.map((damage, index) => (
                        <motion.div 
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + (index * 0.1) }}
                          className="bg-white rounded-lg shadow border border-red-100 overflow-hidden"
                        >
                          {damage.photoUrl && (
                            <div className="relative h-48 overflow-hidden">
                              <img 
                                src={damage.photoUrl} 
                                alt={`Damage ${index + 1}`} 
                                className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                                <span className={`text-xs font-bold px-2 py-1 rounded capitalize ${
                                  damage.severity === 'minor' ? 'bg-yellow-500 text-white' :
                                  damage.severity === 'moderate' ? 'bg-orange-500 text-white' :
                                  'bg-red-600 text-white'
                                }`}>
                                  {damage.severity}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className="p-4">
                            <div className="mb-2">
                              <p className="text-sm text-red-700 font-medium">Damage Location</p>
                              <p className="text-gray-800 font-medium">{damage.location}</p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-red-700 font-medium">Damage Description</p>
                              <p className="text-gray-800">{damage.description}</p>
                            </div>
                            
                            {!damage.photoUrl && (
                              <div className={`mt-2 inline-block px-2 py-1 rounded text-xs font-medium capitalize ${
                                damage.severity === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                                damage.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {damage.severity}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
          
          {/* Documentation Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
          >            <div
              onClick={() => setExpandedSections(prev => ({ ...prev, documentation: !prev.documentation }))}
              className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-6 py-4 border-b border-indigo-200 cursor-pointer flex justify-between items-center"
            >
              <h3 className="font-semibold text-indigo-800 text-lg flex items-center">
                <FaCamera className="mr-2" />
                Documentation
              </h3>
              <motion.div
                animate={{ rotate: expandedSections.documentation ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FaCheck className="text-indigo-800" />
              </motion.div>
            </div>
              <AnimatePresence>
              {expandedSections.documentation && (
                <motion.div 
                  variants={sectionVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  className="p-6 overflow-hidden"
                >                  <div className="grid grid-cols-1 gap-6">
                    {/* Photos with remove functionality */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-indigo-50 border-b border-indigo-100">
                        <div className="flex items-center">
                          <FaCamera className="text-indigo-600 mr-2" />
                          <h4 className="text-lg font-medium text-gray-800">
                            Photos {handover.photos && handover.photos.length > 0 && `(${handover.photos.length})`}
                          </h4>
                        </div>
                      </div>
                      
                      {handover.photos && handover.photos.length > 0 ? (
                        <div className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                            {handover.photos.slice(0, 4).map((photo, index) => (
                              <motion.div 
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                className="relative rounded-lg overflow-hidden shadow-sm border border-gray-200 aspect-square cursor-pointer group"
                              >
                                <div 
                                  className="block h-full"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentPhotoIndex(index);
                                    setCarouselOpen(true);
                                  }}
                                >
                                  <img 
                                    src={getMediaUrl(photo.url)} 
                                    alt={photo.caption || `Photo ${index + 1}`} 
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                {editMode && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveDocumentation('photos', index);
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <FaTrashAlt className="w-3 h-3" />
                                  </button>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                  <p className="text-xs text-white">
                                    {moment(photo.timestamp).format('DD/MM/YYYY')}
                                  </p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                          
                          {/* If more than 4 photos, show View All button */}
                          {handover.photos.length > 4 && (
                            <div className="text-center mt-4">
                              <button
                                onClick={() => {
                                  setCurrentPhotoIndex(0);
                                  setCarouselOpen(true);
                                }}
                                className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                              >
                                View All Photos ({handover.photos.length})
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50">
                          <p className="text-gray-500">No photos available</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Videos with remove functionality */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-indigo-50 border-b border-indigo-100">
                        <div className="flex items-center">
                          <FaVideo className="text-indigo-600 mr-2" />
                          <h4 className="text-lg font-medium text-gray-800">
                            Videos {handover.videos && handover.videos.length > 0 && `(${handover.videos.length})`}
                          </h4>
                        </div>
                      </div>
                      {handover.videos && handover.videos.length > 0 ? (
                        <div className="p-4">                          {/* Show first video with larger display */}
                          <div className="mb-4">
                            <div className="relative rounded-lg overflow-hidden shadow-sm border border-gray-200 group">
                              <video 
                                src={getMediaUrl(handover.videos[0].url)} 
                                controls
                                className="w-full h-auto"
                              ></video>
                              {editMode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveDocumentation('videos', 0);
                                  }}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <FaTrashAlt className="w-4 h-4" />
                                </button>
                              )}
                              <div className="p-2 bg-gray-50">
                                <p className="text-xs text-gray-500">
                                  {handover.videos[0].caption || `Video 1`} - {moment(handover.videos[0].timestamp).format('DD/MM/YYYY')}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* If more than 1 video, show thumbnails for others */}
                          {handover.videos.length > 1 && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">More Videos</h5>                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {handover.videos.slice(1).map((video, index) => (
                                  <div 
                                    key={index}
                                    className="relative rounded-lg overflow-hidden shadow-sm border border-gray-200 cursor-pointer group"
                                  >
                                    <div 
                                      className="aspect-video bg-gray-100 flex items-center justify-center"
                                      onClick={() => window.open(getMediaUrl(video.url), '_blank')}
                                    >
                                      <FaVideo className="text-indigo-400 text-2xl" />
                                    </div>
                                    {editMode && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveDocumentation('videos', index + 1);
                                        }}
                                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <FaTrashAlt className="w-3 h-3" />
                                      </button>
                                    )}
                                    <div className="p-2 bg-gray-50">
                                      <p className="text-xs text-gray-500 truncate">
                                        {video.caption || `Video ${index + 2}`}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50">
                          <p className="text-gray-500">No videos available</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Voice Notes with remove functionality */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-indigo-50 border-b border-indigo-100">
                        <div className="flex items-center">
                          <FaMicrophone className="text-indigo-600 mr-2" />
                          <h4 className="text-lg font-medium text-gray-800">
                            Voice Notes {handover.voiceNotes && handover.voiceNotes.length > 0 && `(${handover.voiceNotes.length})`}
                          </h4>
                        </div>
                      </div>
                      
                      {handover.voiceNotes && handover.voiceNotes.length > 0 ? (
                        <div className="p-4">                          <div className="space-y-4">
                            {handover.voiceNotes.map((note, index) => (
                              <div key={index} className="relative p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group">
                                <div className="flex items-center mb-2">
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                                    <FaMicrophone className="text-indigo-600" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-800">Voice Note {index + 1}</p>
                                    <p className="text-xs text-gray-500">{moment(note.timestamp).format('DD/MM/YYYY HH:mm')}</p>
                                  </div>
                                  {editMode && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveDocumentation('voiceNotes', index);
                                      }}
                                      className="bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <FaTrashAlt className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                <audio 
                                  src={getMediaUrl(note.url)} 
                                  controls
                                  className="w-full mb-2"
                                ></audio>
                                {note.transcript && (
                                  <div className="mt-3 border-t border-gray-200 pt-2">
                                    <p className="text-xs font-medium text-gray-700 mb-1">Transcript:</p>
                                    <p className="text-sm text-gray-600 bg-white p-2 rounded border border-gray-200">{note.transcript}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50">
                          <p className="text-gray-500">No voice notes available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          
          {/* Notes Section with edit functionality */}
          {(handover.notes || editMode) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6"
            >
              <h3 className="font-semibold text-gray-800 text-lg mb-3">
                Notes
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {editMode ? (
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 h-24 bg-white"
                    placeholder="Add handover notes..."
                  />
                ) : (
                  <p className="text-gray-700">{handover.notes || 'No notes'}</p>
                )}
              </div>
            </motion.div>
          )}

        </div>      </motion.div>
      
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <FaExclamationTriangle className="text-red-600 text-2xl" />
                </div>                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Confirm Deletion
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to delete this handover? This action cannot be undone.
                </p>
                <p className="text-gray-600 text-sm bg-yellow-50 p-2 rounded-md border border-yellow-200">
                  <strong>Note:</strong> All associated media files (photos, videos, and voice notes) will also be permanently deleted from the server.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-800 font-medium hover:bg-gray-50 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 bg-red-600 rounded-lg text-white font-medium hover:bg-red-700 transition-colors flex items-center justify-center"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-t-2 border-white border-r-2 border-transparent rounded-full mr-2"
                    />
                  ) : (
                    <FaTrashAlt className="mr-2" />
                  )}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Photo Carousel Modal */}
      <AnimatePresence>
        {carouselOpen && handover?.photos && handover.photos.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center"
            onClick={() => setCarouselOpen(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full h-full flex flex-col items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button 
                className="absolute top-4 right-4 z-10 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 transition-colors"
                onClick={() => setCarouselOpen(false)}
              >
                <FaTimes className="text-xl" />
              </button>
              
              {/* Navigation */}
              <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between px-4 z-10">
                <button 
                  className="text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPhotoIndex(prev => (prev === 0 ? handover.photos.length - 1 : prev - 1));
                  }}
                >
                  <FaArrowLeft />
                </button>
                <button 
                  className="text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPhotoIndex(prev => (prev === handover.photos.length - 1 ? 0 : prev + 1));
                  }}
                >
                  <FaArrowRight />
                </button>
              </div>
              
              {/* Current image */}
              <div className="w-full h-[80vh] flex items-center justify-center">
                <img 
                  src={getMediaUrl(handover.photos[currentPhotoIndex].url)} 
                  alt={handover.photos[currentPhotoIndex].caption || `Photo ${currentPhotoIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              {/* Caption and count */}
              <div className="bg-black bg-opacity-70 text-white p-3 rounded-lg mt-4">
                <p className="text-sm">{handover.photos[currentPhotoIndex].caption || `Photo ${currentPhotoIndex + 1}`}</p>
                <p className="text-xs text-gray-300 mt-1">
                  {moment(handover.photos[currentPhotoIndex].timestamp).format('DD/MM/YYYY HH:mm')}
                  {' '} • {currentPhotoIndex + 1} of {handover.photos.length}
                </p>
              </div>
              
              {/* Thumbnails */}
              {handover.photos.length > 1 && (
                <div className="mt-4 flex overflow-x-auto gap-2 p-2 max-w-full">
                  {handover.photos.map((photo, index) => (
                    <div 
                      key={index}
                      className={`w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 cursor-pointer
                        ${index === currentPhotoIndex ? 'border-blue-500' : 'border-transparent'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentPhotoIndex(index);
                      }}
                    >
                      <img 
                        src={getMediaUrl(photo.url)} 
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showSignaturePreview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            e.stopPropagation();
            closeSignaturePreview();
          }}
        >
          <div 
            className="bg-white p-4 rounded-lg shadow-xl max-w-xl w-full"
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside the modal content from closing it
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Signature Preview</h3>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  closeSignaturePreview();
                }} 
                className="text-black"
              >
                <FaTimes />
              </button>
            </div>
            {currentSignatureUrl ? (
              <img src={getMediaUrl(currentSignatureUrl)} alt="Signature" className="max-w-full h-auto mx-auto" />
            ) : (
              <p>No signature to display.</p>
            )}
            <div className="mt-4 text-right">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeSignaturePreview();
                }}
                className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Add Documentation</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                    <option value="voiceNote">Voice Note</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    accept={
                      uploadType === 'photo' ? 'image/*' :
                      uploadType === 'video' ? 'video/*' : 'audio/*'
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
                  <input
                    type="text"
                    value={uploadCaption}
                    onChange={(e) => setUploadCaption(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Optional caption..."
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-800 font-medium hover:bg-gray-50 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFileUpload}
                  disabled={!uploadFile || uploading}
                  className="flex-1 py-2 bg-blue-600 rounded-lg text-white font-medium hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50"
                >
                  {uploading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-t-2 border-white border-r-2 border-transparent rounded-full mr-2"
                    />
                  ) : (
                    <FaUpload className="mr-2" />
                  )}
                  Upload
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
};

export default HandoverDetailsModal;
